import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DocumentUploadModal } from '../components/DocumentUploadModal';
import SourceSelectionModal from '../components/SourceSelectionModal';
import EditDocumentModal from '../components/EditDocumentModal';
import { ConfirmationModal, PageLoader } from '../components/common';
import { tenantApi, Document, DocumentRequest } from '../lib/tenant-api';
import { PlusIcon, UploadIcon, PencilIcon, TrashIcon, FileTextIcon, SearchIcon, ChevronDown, Check, RefreshCwIcon, Loader2Icon } from 'lucide-react';
import { useEventNotifier } from '../contexts/EventNotifierContext';
import { toast } from 'react-toastify';
import clsx from 'clsx';

const DOCUMENT_TYPE_OPTIONS: { value: Document['document_type']; label: string }[] = [
  { value: 'faq', label: 'FAQ' },
  { value: 'policy', label: 'Policy' },
  { value: 'guide', label: 'Guide' },
  { value: 'manual', label: 'Manual' },
  { value: 'article', label: 'Article' },
  { value: 'other', label: 'Others' },
];

const SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'upload', label: 'Upload' },
  { value: 'hubspot', label: 'Hubspot' },
  { value: 'google_drive', label: 'Google drive' },
  { value: 'evolved_wp', label: 'Evolved WP URL' },
  { value: 'webflow', label: 'Webflow lawma integration' },
  { value: 'shopify', label: 'Shopify webstore' },
];

function documentMatchesSourceFilter(document: Document, filterValue: string): boolean {
  const haystack = `${document.source} ${document.source_file_name || ''} ${document.category || ''}`.toLowerCase();

  switch (filterValue) {
    case 'upload':
      return document.source === 'upload';
    case 'hubspot':
      return haystack.includes('hubspot');
    case 'google_drive':
      return document.source === 'cms' || haystack.includes('google');
    case 'evolved_wp':
      return haystack.includes('evolved') || haystack.includes('wordpress') || haystack.includes(' wp');
    case 'webflow':
      return haystack.includes('webflow');
    case 'shopify':
      return haystack.includes('shopify');
    default:
      return document.source === filterValue;
  }
}

function getDocumentSourceLabel(document: Document): string {
  const haystack = `${document.source_file_name || ''} ${document.category || ''}`.toLowerCase();

  if (document.source === 'upload') return 'Upload';
  if (haystack.includes('hubspot')) return 'Hubspot';
  if (document.source === 'cms' || haystack.includes('google')) return 'Google drive';
  if (haystack.includes('evolved') || haystack.includes('wordpress') || haystack.includes(' wp')) {
    return 'Evolved WP URL';
  }
  if (haystack.includes('webflow')) return 'Webflow lawma integration';
  if (haystack.includes('shopify')) return 'Shopify webstore';
  if (document.source === 'manual') return 'Manual';
  if (document.source === 'api') return 'API';
  if (document.source === 'integration') return 'Integration';

  return String(document.source);
}

const TAG_COLORS = [
  'bg-blue-100 text-blue-800',
  'bg-purple-100 text-purple-800',
  'bg-indigo-100 text-indigo-800',
  'bg-rose-100 text-rose-800',
];

const DOCUMENTS_TOOLBAR_HEIGHT = 'h-10';

function FilterDropdown({
  buttonLabel,
  panelTitle,
  options,
  selected,
  onApply,
}: {
  buttonLabel: string;
  panelTitle: string;
  options: { value: string; label: string }[];
  selected: string[];
  onApply: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string[]>(selected);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPending(selected);
  }, [selected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setPending(selected);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selected]);

  const toggleValue = (value: string) => {
    setPending((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const triggerLabel =
    selected.length === 0
      ? buttonLabel
      : selected.length === 1
        ? options.find((option) => option.value === selected[0])?.label || buttonLabel
        : `${selected.length} selected`;

  return (
    <div ref={containerRef} className="relative w-full shrink-0 lg:w-44">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(
          'inline-flex w-full items-center justify-between rounded-sm bg-gray-100 px-4 text-sm font-medium text-gray-700 hover:bg-gray-200',
          DOCUMENTS_TOOLBAR_HEIGHT
        )}
      >
        <span>{triggerLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-60 rounded-sm border border-gray-200 bg-gray-50 p-4 shadow-md">
          <p className="mb-3 text-sm font-semibold text-gray-900">{panelTitle}</p>
          <div className="max-h-52 space-y-2.5 overflow-y-auto">
            {options.map((option) => {
              const isChecked = pending.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleValue(option.value)}
                  className="flex w-full cursor-pointer items-center gap-3 text-left"
                >
                  <span
                    className={clsx(
                      'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                      isChecked
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : 'border-gray-300 bg-white'
                    )}
                  >
                    {isChecked && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className="text-sm text-gray-600">{option.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => {
                onApply(pending);
                setOpen(false);
              }}
              className="rounded-sm bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const KnowledgeBaseManagement: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isSourceSelectionOpen, setIsSourceSelectionOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState<Document | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);

  const { subscribe } = useEventNotifier();

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe('refresh', (data) => {
      if (data.message == 'documents') {
        loadDocuments();
      }
    });

    return () => unsubscribe();
  }, [subscribe]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await tenantApi.getDocuments();
      setDocuments(data);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error
            ? err.message
            : 'Failed to load documents';
      setError(message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = useMemo(() => {
    return documents.filter((document) => {
      const matchesSearch =
        document.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (document.content && document.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (document.category && document.category.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesType =
        selectedTypes.length === 0 || selectedTypes.includes(document.document_type);

      const matchesSource =
        selectedSources.length === 0 ||
        selectedSources.some((filterValue) => documentMatchesSourceFilter(document, filterValue));

      return matchesSearch && matchesType && matchesSource;
    });
  }, [documents, searchTerm, selectedTypes, selectedSources]);

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    try {
      setIsDeleting(true);
      await tenantApi.deleteDocument(documentToDelete.id);

      try {
        await tenantApi.deleteEmbeddings('document', documentToDelete.id);
      } catch (embeddingError) {
        console.warn(`Failed to delete embeddings for document ${documentToDelete.id}:`, embeddingError);
      }

      await loadDocuments();
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
      setError(null);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error
            ? err.message
            : 'Failed to delete document';
      setError(message || 'Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerateEmbeddings = async (document: Document) => {
    try {
      setGeneratingId(document.id);
      await tenantApi.regenerateSelectedEmbeddings({
        content_types: ['document'],
        content_ids: [document.id],
        replace_existing: true,
      });
      toast.success(`Embedding generation started for "${document.title}".`);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : err instanceof Error
            ? err.message
            : 'Failed to start embedding generation';
      toast.error(message || 'Failed to start embedding generation');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleEditDocument = (document: Document) => {
    setDocumentToEdit(document);
    setEditModalOpen(true);
  };

  const handleSaveDocument = async (documentData: DocumentRequest) => {
    if (!documentToEdit) return;

    await tenantApi.updateDocument(documentToEdit.id, documentData);
    await loadDocuments();
    setEditModalOpen(false);
    setDocumentToEdit(null);
    setError(null);
  };

  const formatContent = (content: string) => {
    try {
      const parsed = JSON.parse(content);
      if (typeof parsed === 'object') {
        return `${JSON.stringify(parsed).substring(0, 280)}...`;
      }
      return content;
    } catch {
      return content;
    }
  };

  const getDocumentTypeColor = (type: string) => {
    switch (type) {
      case 'faq':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'policy':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'guide':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'manual':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'article':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'upload':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'manual':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'cms':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'api':
        return 'bg-pink-100 text-pink-800 border-pink-200';
      case 'integration':
        return 'bg-violet-100 text-violet-800 border-violet-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTypeLabel = (type: string) =>
    DOCUMENT_TYPE_OPTIONS.find((option) => option.value === type)?.label ||
    type.charAt(0).toUpperCase() + type.slice(1);

  const EmptyState = () => (
    <div className="py-12 text-center">
      <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">No documents found</h3>
      <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">
        Get started by uploading a file or creating your first knowledge base document.
      </p>
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setUploadModalOpen(true)}
          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Upload file
        </button>
      </div>
    </div>
  );

  const SearchEmptyState = () => (
    <div className="py-12 text-center">
      <SearchIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">No documents match your filters</h3>
      <p className="mt-2 text-sm text-gray-500">Try adjusting your search or filter selections.</p>
      <div className="mt-6">
        <button
          type="button"
          onClick={() => {
            setSearchTerm('');
            setSelectedTypes([]);
            setSelectedSources([]);
          }}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Clear filters
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage your documents including FAQs, policies, guides, manuals, and articles.
            </p>
          </div>
          <div className="flex shrink-0 gap-3">
            <button
              type="button"
              onClick={() => setUploadModalOpen(true)}
              className="inline-flex items-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              <UploadIcon className="mr-2 h-4 w-4" />
              Upload file
            </button>
            <button
              type="button"
              onClick={() => setIsSourceSelectionOpen(true)}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              Add document
            </button>
          </div>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-2 shadow">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-stretch">
            <div className="relative min-w-0 flex-1">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={clsx(
                  'w-full rounded-sm border-0 bg-gray-50 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0',
                  DOCUMENTS_TOOLBAR_HEIGHT
                )}
                placeholder="Search Documents"
              />
            </div>
            <FilterDropdown
              buttonLabel="All types"
              panelTitle="Filter by Types"
              options={DOCUMENT_TYPE_OPTIONS}
              selected={selectedTypes}
              onApply={setSelectedTypes}
            />
            <FilterDropdown
              buttonLabel="All sources"
              panelTitle="Filter by Source"
              options={SOURCE_OPTIONS}
              selected={selectedSources}
              onApply={setSelectedSources}
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex h-[400px] items-center justify-center">
            <PageLoader />
          </div>
        ) : documents.length === 0 ? (
          <EmptyState />
        ) : filteredDocuments.length === 0 ? (
          <SearchEmptyState />
        ) : (
          <div className="space-y-4">
            {filteredDocuments.map((document) => (
              <div
                key={document.id}
                className="rounded-md border border-gray-200 bg-white p-4 hover:bg-gray-50"
              >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-bold text-gray-900">{document.title}</h3>
                      <p className="mt-2 line-clamp-4 text-sm text-gray-600">
                        {formatContent(document.content)}
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-gray-500">
                        <span className="inline-flex items-center gap-2">
                          <span className="font-medium text-gray-600">Type:</span>
                          <span
                            className={clsx(
                              'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                              getDocumentTypeColor(document.document_type)
                            )}
                          >
                            {formatTypeLabel(document.document_type)}
                          </span>
                        </span>

                        <span className="inline-flex items-center gap-2">
                          <span className="font-medium text-gray-600">Source:</span>
                          <span
                            className={clsx(
                              'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                              getSourceColor(document.source)
                            )}
                          >
                            {getDocumentSourceLabel(document)}
                          </span>
                        </span>

                        {document.tags && document.tags.length > 0 && (
                          <span className="inline-flex flex-wrap items-center gap-2">
                            <span className="font-medium text-gray-600">Tags:</span>
                            {document.tags.map((tag, index) => (
                              <span
                                key={tag}
                                className={clsx(
                                  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                                  TAG_COLORS[index % TAG_COLORS.length]
                                )}
                              >
                                {tag}
                              </span>
                            ))}
                          </span>
                        )}

                        <span className="inline-flex items-center gap-2">
                          <span className="font-medium text-gray-600">Created:</span>
                          <span className="text-gray-700">
                            {new Date(document.created_at).toLocaleDateString()}
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleGenerateEmbeddings(document)}
                        disabled={generatingId === document.id}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
                        title="Generate / regenerate embeddings"
                      >
                        {generatingId === document.id ? (
                          <Loader2Icon className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCwIcon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditDocument(document)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                        title="Edit document"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDocumentToDelete(document);
                          setDeleteModalOpen(true);
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-red-600"
                        title="Delete document"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <DocumentUploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUploadComplete={() => {
          loadDocuments();
          setUploadModalOpen(false);
        }}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteDocument}
        title="Delete Document"
        message={`Are you sure you want to delete the document "${documentToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {documentToEdit && (
        <EditDocumentModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleSaveDocument}
          document={documentToEdit}
        />
      )}

      <SourceSelectionModal
        isOpen={isSourceSelectionOpen}
        onClose={() => setIsSourceSelectionOpen(false)}
        contentType="documents"
        onManualUpload={() => setUploadModalOpen(true)}
      />
    </>
  );
};

export default KnowledgeBaseManagement;
